import React, { useContext, useEffect, useRef, useState } from 'react';
import Dialog from '#lib/components/dialog.jsx';
import Button from '#lib/components/button.jsx';
import { addClasses, cn, mergeClasses } from '#lib/helpers/class.js';
import Input from '#lib/components/input.jsx';
import File from '#lib/components/file-dnd.jsx';
import { Spinner } from '#lib/components/loading.jsx';
import { Icon } from '@iconify-icon/react';
import Select from '#lib/components/select.jsx';
import { defaultAccessValue, defaultDisplayValue, defaultMakeReactKey } from '#helpers/helpers.js';
import { useImageLibrary } from '#hooks/use-image-library.js';
import FloatingTooltip from '../tooltip.jsx';
import { UserContext } from '#src/index.jsx';
import ImageManipulator from './image-manipulator.jsx';
import { useGalleryPanel } from '#hooks/use-gallery-panel.js';
import { isFunction } from '#lib/utils/function.js';
import ImagePreview from './image-preview.jsx';

export function GalleryPanel(props) {
    const { files, defaultState, onChangePreview, isOpen, onClose, multiple } = props;

    const { getLanguage } = useContext(UserContext);

    // It needs to fetch images from the external library api
    const imageLibraryProps = useImageLibrary({ multiple, lang: getLanguage() });

    const imagePanelProps = useGalleryPanel({
        files,
        defaultState,
        handleChangePreview: onChangePreview,
        isOpen,
        onClose,
        ...imageLibraryProps
    });

    const { state, setIsLoading, handleChangePanel, handleUpdateFiles, handleClose } = imagePanelProps;
    let sectionText, content;
    switch (state) {
        case 'selecting':
            sectionText = _t.edit.imagePanelText();
            content = <SelectionSection onClick={handleChangePanel} handleUpdateFiles={handleUpdateFiles} />;
            break;
        case 'uploading':
            sectionText = _t.edit.imagePanelText();
            content = <LoaderSection {...props} {...imagePanelProps} {...imageLibraryProps} />;
            break;
        case 'editing':
            sectionText = _t.edit.imagePanelText();
            content = <EditSection {...props} {...imagePanelProps} />;
            break;
    }

    return (
        <Dialog isOpen={isOpen ?? false}>
            <Dialog.Panel static className="flex flex-col max-h-full space-y-4 select-none">
                <div className="pr-4">
                    <Dialog.CloseButton
                        onClick={() => {
                            handleClose(true);
                            setIsLoading(false);
                        }}
                    />
                    <b>{_t.edit.imagePanelText()}</b>
                    <p className="pr-2 mt-1 text-sm">{sectionText}</p>
                </div>
                {content}
            </Dialog.Panel>
        </Dialog>
    );
}

// state === "selecting"
function SelectionSection({ onClick, handleUpdateFiles }) {
    const OPTIONS = [
        {
            id: 'U',
            value: _t.global.importOptions.device()
        },
        {
            id: 'L',
            value: _t.global.importOptions.library()
        },
        {
            id: 'E',
            value: _t.global.importOptions.url()
        },
        {
            id: 'D',
            value: _t.global.importOptions.aiImage()
        }
    ];

    return (
        <div className="flex justify-between gap-4">
            {OPTIONS.map((option) => {
                return (
                    <div
                        key={option.id}
                        className="relative flex flex-col items-center w-24 gap-3 px-2 py-4 border rounded-lg cursor-pointer bg-slate-100 border-slate-200 hover:bg-primary-50 hover:border-primary-400"
                        onClick={() => onClick(option.id)}
                    >
                        {option.id === 'U' && (
                            <>
                                <Icon
                                    icon="material-symbols:computer-outline"
                                    width="1.2rem"
                                    height="1.2rem"
                                    className="pt-[3px]"
                                />
                                <input
                                    type="file"
                                    onChange={handleUpdateFiles}
                                    className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                                    accept="image/png, image/jpeg"
                                    multiple={true}
                                />
                            </>
                        )}
                        {option.id === 'L' && (
                            <Icon
                                icon="clarity:image-gallery-solid"
                                width="1.4rem"
                                height="1.4rem"
                                className="pt-[3px]"
                            />
                        )}
                        {option.id === 'E' && (
                            <Icon icon="ph:globe" width="1.4rem" height="1.4rem" className="pt-[3px]" />
                        )}
                        {option.id === 'D' && (
                            <Icon icon="mdi:robot-outline" width="1.2rem" height="1.2rem" className="pt-[3px]" />
                        )}

                        <p className="text-xs">{option.value}</p>
                    </div>
                );
            })}
        </div>
    );
}

// state === "uploading"
function LoaderSection(props) {
    const {
        optionId,
        name,
        onlyPreview,
        editingFile,
        isEdited,
        handleUpdateFiles,
        isLoading,
        width,
        height,
        isSquare,
        libraries,
        images,
        handleSelectLibrary,
        currentLibrary,
        inputValue,
        hasError,
        handleChangeText,
        handleClear,
        handleSelectLibraryImage,
        handleGenerateImage
    } = props;

    const baseStyle = {
        width,
        height,
        marginLeft: 'auto',
        marginRight: 'auto'
    };
    const style = {
        ...baseStyle,
        aspectRatio: isSquare ? '1/1' : 'none'
    };

    return (
        <>
            {optionId && optionId !== 'U' && (
                <div className="flex items-start justify-end gap-2">
                    {optionId === 'L' && (
                        <>
                            <Select.Full
                                options={libraries}
                                makeReactKey={defaultMakeReactKey}
                                accessValue={defaultAccessValue}
                                displayValue={defaultDisplayValue}
                                onChange={handleSelectLibrary}
                                value={currentLibrary}
                                className={addClasses('cursor-pointer')}
                                placeholder={_t.edit.selectLibrary()}
                                name="library"
                                inPortal={false}
                            />
                            <Input.Full
                                className="w-full h-full"
                                placeholder={_t.edit.enterKeyword()}
                                value={inputValue}
                                hasError={hasError}
                                onChange={handleChangeText}
                            />
                        </>
                    )}
                    {optionId === 'E' && (
                        <Input.Full
                            className="w-full h-full"
                            placeholder={_t.edit.enterUrl()}
                            value={inputValue}
                            hasError={hasError}
                            onChange={handleChangeText}
                        />
                    )}
                    {optionId === 'D' && (
                        <Input.Full
                            className="w-full h-full"
                            placeholder={_t.edit.writePrompt()}
                            value={inputValue}
                            hasError={hasError}
                            onChange={handleChangeText}
                        />
                    )}
                    <Button look="primary" type="submit" disabled={isLoading} onClick={handleGenerateImage}>
                        {optionId === 'E' && _t.global.add()}
                        {optionId === 'L' && _t.global.search()}
                        {optionId === 'D'
                            ? editingFile
                                ? _t.global.regenerate()
                                : isLoading
                                ? _t.global.loading()
                                : _t.global.generate()
                            : null}
                    </Button>
                </div>
            )}
            <div className="relative flex justify-center overflow-hidden rounded-md bg-neutral-100" style={style}>
                {isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center h-full">
                        <Spinner />
                    </div>
                )}
                {optionId === 'U' && (
                    <File
                        key={optionId}
                        name={name}
                        onUpdateFiles={handleUpdateFiles}
                        className={addClasses('h-full w-full')}
                        accept="image/png, image/jpeg"
                        sourceType={optionId}
                        multiple={true}
                        {...props}
                    />
                )}
                {optionId === 'L' && images?.length > 0 && (
                    <ImageLibrarySection
                        className={addClasses(`w-[${width ?? '480'}px] h-[${height ?? '270'}px]`)}
                        style={style}
                        {...props}
                    />
                )}
                {/* {!isLoading && (optionId !== "U") && <p className="w-full text-center text-neutral-700">{_t.global.noContent()}</p>} */}
            </div>

            <div className="flex justify-end gap-2">
                {!onlyPreview && (
                    <Button type="submit" onClick={handleClear} disabled={isLoading}>
                        {editingFile ? _t.global.change() : _t.global.cancel()}
                    </Button>
                )}
                <Button look="primary" type="submit" onClick={handleSelectLibraryImage} disabled={!isEdited}>
                    {_t.global.confirm()}
                </Button>
            </div>
        </>
    );
}

// state === "editing"
function EditSection(props) {
    const {
        editingFiles: files,
        setState,
        initialActiveIndex,
        handleInitCoords,
        handleClear,
        handleSubmit,
        width,
        height,
        isLoading,
        isEdited,
        isSquare,
        rounded,
        handleDeleteEditingFile,
        handlePinFile,
        onDelete,
        onPin
    } = props;

    const baseStyle = {
        width,
        height,
        marginLeft: 'auto',
        marginRight: 'auto'
    };
    const style = {
        ...baseStyle,
        aspectRatio: isSquare ? '1/1' : 'none'
    };

    const [active, setActive] = useState(initialActiveIndex);
    const [activeImage, setActiveImage] = useState(files[active]);
    const showPrevImage = () => {
        setActive((prev) => (prev > 0 ? prev - 1 : files.length - 1));
    };
    const showNextImage = () => {
        setActive((prev) => (prev < files.length - 1 ? prev + 1 : 0));
    };
    const handleDelete = () => {
        if (!confirm(_t.global.confirmDelete())) return;
        handleDeleteEditingFile(active);
        if (active === files.length - 1) setActive((prev) => prev - 1);
        if (files.length <= 1) handleClear();
        if (isFunction(onDelete)) onDelete(active);
    };
    const pinImage = () => {
        handlePinFile(active);
        setActive(0);
        if (isFunction(onPin)) onPin(active);
    };
    useEffect(() => {
        if (galleryRef.current) {
            galleryRef.current.scrollTo({
                left: active === 0 ? 0 : (active - 1) * 150,
                behavior: 'smooth'
            });
        }
        setActiveImage(() => files[active]);
    }, [files.length, active]);

    const galleryRef = useRef(null);

    return (
        <div>
            <div
                className="relative flex items-center justify-center overflow-hidden border rounded-md bg-neutral-100 border-neutral-300"
                style={style}
            >
                <div className="relative grid w-full rounded-md aspect-video">
                    <DeleteButton onDelete={handleDelete} />
                    {active === 0 ? <PinnedIndicator /> : <PinButton onPin={pinImage} />}

                    <div className="absolute top-0 left-0 flex items-center w-full h-full overflow-hidden rounded-md">
                        <Button
                            className={addClasses(`absolute z-50 left-4`)}
                            look="circular"
                            size="xs"
                            onClick={showPrevImage}
                        >
                            <Icon icon="material-symbols:arrow-left-alt-rounded" width="1.2rem" height="1.2rem" />
                        </Button>
                        <Button
                            className={addClasses(`absolute z-50 right-4`)}
                            look="circular"
                            size="xs"
                            onClick={showNextImage}
                        >
                            <Icon icon="material-symbols:arrow-right-alt-rounded" width="1.2rem" height="1.2rem" />
                        </Button>
                        {activeImage && (
                            <div className="absolute z-50 -translate-x-1/2 left-1/2 bottom-3">
                                <span className="text-neutral-100">
                                    {active + 1} / {files.length}
                                </span>
                            </div>
                        )}
                    </div>
                    {activeImage && (
                        <ImageManipulator
                            key={active}
                            index={active}
                            image={activeImage}
                            containerWidth={width}
                            containerHeight={height}
                            initialState={activeImage?.coords}
                            onTransformChange={(v) => handleInitCoords(v, active)}
                            rounded={rounded}
                            setState={setState}
                        />
                    )}
                </div>
            </div>
            {files.length > 1 && (
                <div ref={galleryRef} className={'flex gap-2 h-24 w-[480px] overflow-x-auto py-1'}>
                    {files.map((file, index) => {
                        const isActive = active === index;
                        return (
                            <div
                                key={file.src}
                                className={cn('h-full min-w-[145px] cursor-pointer', !isActive && 'opacity-50')}
                                onClick={() => setActive(index)}
                            >
                                <ImagePreview imageSrc={file.src} initialState={file.coords} />
                            </div>
                        );
                    })}
                </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
                <Button type="submit" onClick={handleClear} disabled={isLoading}>
                    {_t.global.add()}
                </Button>
                <Button look="primary" type="submit" onClick={handleSubmit} disabled={!isEdited}>
                    {_t.global.confirm()}
                </Button>
            </div>
        </div>
    );
}

function DeleteButton({ onDelete }) {
    return (
        <Button className={addClasses(`absolute top-4 right-4 z-50`)} look="circular" size="xs" onClick={onDelete}>
            <Icon className="cursor-pointer" icon="ph:trash" width="1.2rem" height="1.2rem" />
        </Button>
    );
}

function PinnedIndicator() {
    return (
        <Button
            className={addClasses(`absolute top-4 right-[54px] z-50 events-none cursor-default`)}
            look="circular"
            size="xs"
        >
            <Icon
                icon="ph:push-pin-fill"
                width="1.2rem"
                height="1.2rem"
                style={{
                    rotate: '-45deg'
                }}
            />
        </Button>
    );
}

function PinButton({ onPin }) {
    return (
        <Button className={addClasses(`absolute top-4 right-[54px] z-50`)} look="circular" size="xs" onClick={onPin}>
            <Icon icon="ph:push-pin" width="1.2rem" height="1.2rem" />
        </Button>
    );
}

function ImageLibrarySection(props) {
    const {
        inputValue,
        images,
        selectedImages,
        lastSearch,
        loading,
        error,
        fetchImagesFromLibrary,
        selectImage,
        library,
        className
    } = props;

    const { lastPage } = lastSearch;
    const [hoveredImg, setHoveredImg] = useState(null);
    const [loadBtn, setLoadBtn] = useState(false);

    const onClick = () => {
        setLoadBtn(true);
        fetchImagesFromLibrary(inputValue, lastPage + 1);
    };

    useEffect(() => {
        setLoadBtn(false);
    }, [images]);

    return (
        <div className={mergeClasses('relative grid grid-cols-3 gap-1 bg-neutral-100 overflow-auto', className)}>
            {!images.length && !loading && !error && (
                <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full p-2">
                    <p className="text-center text-neutral-700">{_t.edit.promptToSearch()}</p>
                </div>
            )}

            {loading && (
                <div className="absolute inset-0 flex items-center justify-center h-full">
                    <Spinner />
                </div>
            )}
            {images &&
                images?.map((image) => {
                    const isSelected = selectedImages.some((img) => img.id === image.id);
                    return (
                        <FloatingTooltip
                            key={image?.id}
                            content={`created by '${image.author}' on ${library}`}
                            placement={'top'}
                        >
                            <div
                                className="col-span-1 relative pt-[56%] overflow-hidden"
                                onMouseOver={() => setHoveredImg(image.id)}
                                onMouseOut={() => setHoveredImg(null)}
                            >
                                <Thumbnail src={image.thumbnailSrc} />
                                <div
                                    className={cn(
                                        'transition-all absolute w-full h-full top-0 cursor-pointer',
                                        hoveredImg !== image.id || isSelected ? 'bg-[#00000044]' : 'bg-transparent',
                                        isSelected && 'bg-transparent border-[6px] border-primary-300'
                                    )}
                                    onClick={() => selectImage(image)}
                                ></div>
                            </div>
                        </FloatingTooltip>
                    );
                })}

            {!error && images && images.length !== 0 && (
                <div className="flex justify-center col-span-3">
                    <Button className="w-full my-2 font-bold text-primary-600" busy={loadBtn} onClick={onClick}>
                        {_t.components.table.loadMore()}
                    </Button>
                </div>
            )}
            {error && !loading && (
                <div className="w-full col-span-3">
                    <p className="flex items-center justify-center w-full h-full p-4 text-lg font-bold text-neutral-600">
                        {_t.components.table.noMore()}
                    </p>
                </div>
            )}
        </div>
    );
}

function Thumbnail(props) {
    const { src, ...rest } = props;
    return <img className="absolute top-0 object-cover object-center w-full h-full" src={src} {...rest} />;
}
